import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { environment } from '../../../environments/environment';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { ServerInfo, ServerInfoService } from './server-info.service';
import { parseReleaseJournal } from './release-journal';

interface ConfigRow {
  key: string;
  value: string;
  masked: boolean;
}

interface ConfigSection {
  name: string;
  rows: ConfigRow[];
}

const MASK = '********';

// Page /server_info (administrateurs) : la réponse de GET /info mise en forme — versions, release en production, journal, configuration.
@Component({
  selector: 'app-server-info',
  standalone: true,
  templateUrl: './server-info.component.html',
  styleUrls: ['./server-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MenuCommonComponent, MatButtonModule, MatIconModule],
})
export class ServerInfoComponent implements OnInit {
  private service = inject(ServerInfoService);
  private destroyRef = inject(DestroyRef);

  readonly frontVersion = environment.appVersion;
  readonly fselibVersion = environment.fselibVersion;
  readonly serverUrl = environment.server_url;

  readonly info = signal<ServerInfo | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly journal = computed(() => parseReleaseJournal(this.info()?.release ?? ''));
  readonly current = computed(() => this.journal().entries.at(-1) ?? null);
  // Le fichier va du plus ancien au plus récent ; l'écran montre la dernière mise en production en tête.
  readonly history = computed(() => [...this.journal().entries].reverse());
  readonly configSections = computed(() => configSectionsOf(this.info()?.config ?? null));
  readonly rawJson = computed(() => JSON.stringify(this.info(), null, 2));

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.service
      .info$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: info => {
          this.info.set(info);
          this.loading.set(false);
        },
        error: (e: HttpErrorResponse) => {
          this.error.set(failureOf(e));
          this.loading.set(false);
        },
      });
  }
}

function failureOf(e: HttpErrorResponse): string {
  if (e.status === 401) return 'Session expirée : reconnectez-vous.';
  if (e.status === 403) return 'Réservé aux administrateurs.';
  if (e.status === 0) return `Serveur injoignable (${environment.server_url}).`;
  return `Le serveur a répondu ${e.status} ${e.statusText ?? ''}`.trim();
}

// Une section par clé de premier niveau ; les tables imbriquées s'aplatissent en clés pointées, les scalaires isolés vont dans « Général ».
function configSectionsOf(config: Record<string, unknown> | null): ConfigSection[] {
  if (!config) return [];
  const general: ConfigRow[] = [];
  const sections: ConfigSection[] = [];
  for (const [name, value] of Object.entries(config)) {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      sections.push({ name, rows: flatten(value as Record<string, unknown>, '') });
    } else {
      general.push(rowOf(name, value));
    }
  }
  sections.sort((a, b) => a.name.localeCompare(b.name));
  return general.length ? [{ name: 'Général', rows: general }, ...sections] : sections;
}

function flatten(obj: Record<string, unknown>, prefix: string): ConfigRow[] {
  return Object.entries(obj)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([k, v]) => (v !== null && typeof v === 'object' && !Array.isArray(v) ? flatten(v as Record<string, unknown>, `${prefix}${k}.`) : [rowOf(prefix + k, v)]));
}

function rowOf(key: string, value: unknown): ConfigRow {
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return { key, value: text, masked: text === MASK };
}
